import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useRecalculateDrawdown } from '@/hooks/react-query/useDrawdownBatches';
import { useDrawdownRecalculationData } from './hooks/useDrawdownRecalculationData';
import { useInvestorSelection } from './hooks/useInvestorSelection';
import { DrawdownDateInputs } from './components/DrawdownDateInputs';
import { CalculationSummary } from './components/CalculationSummary';
import { NewInvestorsSection } from './components/NewInvestorsSection';
import { ExistingInvestorsSection } from './components/ExistingInvestorsSection';
import { useModalKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';

interface DrawdownRecalculationModalProps {
  open: boolean;
  onClose: () => void;
  fundId: string;
  drawdownId: string;
  onSuccess?: () => void;
}

export function DrawdownRecalculationModal({
  open,
  onClose,
  fundId,
  drawdownId,
  onSuccess,
}: DrawdownRecalculationModalProps) {
  // Prevent keyboard shortcuts (especially ESC) from parent components when modal is open
  useModalKeyboardShortcuts('drawdown-recalculation-modal', open);

  const recalculateMutation = useRecalculateDrawdown();

  // Custom hook for data fetching and state management
  const {
    loadingInvestors,
    loadingBatches,
    error,
    setError,
    availableInvestors,
    setAvailableInvestors,
    existingInvestors,
    drawdownDates,
    drawdownInfo,
    dueDate,
    setDueDate,
  } = useDrawdownRecalculationData({ open, fundId, drawdownId });

  // Custom hook for investor selection logic
  const { handleToggleInvestor, handleToggleAll, selectedCount, summary } =
    useInvestorSelection({
      availableInvestors,
      existingInvestors,
      setAvailableInvestors,
    });
  const addInvestorsLabel = `Add ${selectedCount} Investor${selectedCount !== 1 ? 's' : ''}`;

  const handleRecalculate = async () => {
    setError(null);

    try {
      const selectedInvestors = availableInvestors.filter(
        (inv) => inv.isSelected
      );

      if (selectedInvestors.length === 0) {
        setError('Please select at least one investor to add');
        return;
      }

      if (!drawdownDates) {
        setError('Unable to determine drawdown dates');
        return;
      }

      if (!dueDate) {
        setError('Please select a due date');
        return;
      }

      const addedInvestors = selectedInvestors.map((inv) => ({
        investor_id: inv.investor_id,
        notice_date: drawdownDates.issue_date,
        due_date: dueDate,
      }));

      await recalculateMutation.mutateAsync({
        fundId,
        drawdownId,
        data: {
          added_investors: addedInvestors,
          preserve_sent_notices: true,
          regenerate_dates: false,
        },
      });

      // Success! Call the success callback and close
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to recalculate drawdown'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            Recalculate Drawdown
            {drawdownInfo && (
              <span className="font-normal text-muted-foreground">
                {' '}
                - {drawdownInfo.purpose}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {drawdownInfo?.description
              ? drawdownInfo.description
              : 'Add new investors to this drawdown'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Drawdown Dates Section */}
          <DrawdownDateInputs
            drawdownDates={drawdownDates}
            dueDate={dueDate}
            onDueDateChange={setDueDate}
          />

          {/* Calculation Summary Section */}
          <CalculationSummary
            summary={summary}
            selectedCount={selectedCount}
            existingInvestors={existingInvestors}
          />

          {/* New Investors Section */}
          <NewInvestorsSection
            availableInvestors={availableInvestors}
            loadingInvestors={loadingInvestors}
            onToggleInvestor={handleToggleInvestor}
            onToggleAll={handleToggleAll}
          />

          {/* Separator */}
          {availableInvestors.length > 0 && existingInvestors.length > 0 && (
            <Separator />
          )}

          {/* Existing Investors Section */}
          <ExistingInvestorsSection
            existingInvestors={existingInvestors}
            loadingBatches={loadingBatches}
          />

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={recalculateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRecalculate}
              disabled={
                recalculateMutation.isPending ||
                selectedCount === 0 ||
                loadingInvestors
              }
            >
              {recalculateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding Investors...
                </>
              ) : (
                addInvestorsLabel
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
